// Escala de cores de OS compartilhada pelos mapas (dashboard, /maps/service_orders,
// show da OS). Modos: 'urgency' (última fiscalização), 'creation_date' (criação da OS)
// e 'street_type'. Urgency e creation_date usam a mesma escala; só muda a data de referência.
//
// markerBucket() é a base: devolve a faixa da OS ({ key, color, group, label }). A cor do
// marcador e o modelo da legenda (com contagem) saem daí, então filtro e pintura nunca divergem.
// A legenda clicável que consome esse modelo está em maps/service_order_map_legend.js.

const GREEN = '#00ff00';
const YELLOW = '#ffff00';
const ORANGE = '#ffa500';
const RED = '#ff0000';
const GRAY = '#808080';
const DAY_MS = 1000 * 60 * 60 * 24;

// Fallback para vias sem tipo/prazos configurados
const FALLBACK_LEVELS = { level1: 10, level2: null, level3: 15 };

const NO_STREET_TYPE = 'Sem tipo definido';
const FINISHED_KEY = 'finished';
const FINISHED_LABEL = 'OSs Concluídas';
const LEVEL_COLORS = { 1: GREEN, 2: YELLOW, 3: ORANGE, 4: RED };

function levelsFor(serviceOrder) {
  const level1 = serviceOrder.street_type_urgency_level_1_days;
  const level2 = serviceOrder.street_type_urgency_level_2_days;
  const level3 = serviceOrder.street_type_urgency_level_3_days;

  if (level1 && level2 && level3) {
    return { level1: level1, level2: level2, level3: level3 };
  }

  return FALLBACK_LEVELS;
}

function levelForDays(daysDiff, levels) {
  if (daysDiff <= levels.level1) return 1;
  if (levels.level2 && daysDiff <= levels.level2) return 2;
  if (daysDiff <= levels.level3) return 3;
  return 4;
}

function levelLabel(levels, level) {
  if (level === 4) {
    return '> ' + levels.level3 + ' dias';
  }
  const days = level === 1 ? levels.level1 : (level === 2 ? levels.level2 : levels.level3);
  return '≤ ' + days + ' dias';
}

// Faixa de legenda a que a OS pertence: { key, color, group, label }.
// `key` identifica a linha da legenda e é o que o filtro por cor liga/desliga.
function markerBucket(serviceOrder, mode, currentDate, options = {}) {
  if (options.grayFinished && serviceOrder.status === 'finished') {
    return { key: FINISHED_KEY, color: GRAY, group: null, label: FINISHED_LABEL };
  }

  if (mode === 'street_type') {
    const name = serviceOrder.street_type_name || NO_STREET_TYPE;
    return {
      key: 'street_type|' + name,
      color: serviceOrder.street_type_color || GRAY,
      group: null,
      label: name
    };
  }

  const group = serviceOrder.street_type_name || NO_STREET_TYPE;
  const levels = levelsFor(serviceOrder);
  const rawDate = mode === 'creation_date' ? serviceOrder.created_at : serviceOrder.last_inspection_date;
  const level = rawDate
    ? levelForDays(Math.floor((currentDate - new Date(rawDate)) / DAY_MS), levels)
    : 4;

  return {
    key: group + '|' + level,
    color: LEVEL_COLORS[level],
    group: group,
    label: levelLabel(levels, level)
  };
}

function markerColor(serviceOrder, mode, currentDate, options = {}) {
  return markerBucket(serviceOrder, mode, currentDate, options).color;
}

// Modelo da legenda a partir da lista de OSs, com contagem por faixa:
// [{ name: 'Via Principal'|null, items: [{ key, color, label, count }] }]
// Contagens vêm sempre do conjunto completo de dados, nunca do filtrado.
function legendModelFrom(data, mode, currentDate, options = {}) {
  const counts = {};
  const streetTypeColors = {};
  const urgencyLevels = {};
  let finishedCount = 0;

  data.forEach((serviceOrder) => {
    const bucket = markerBucket(serviceOrder, mode, currentDate, options);
    counts[bucket.key] = (counts[bucket.key] || 0) + 1;

    if (bucket.key === FINISHED_KEY) {
      finishedCount += 1;
    } else if (mode === 'street_type') {
      streetTypeColors[bucket.label] = bucket.color;
    } else {
      urgencyLevels[bucket.group] = levelsFor(serviceOrder);
    }
  });

  const groups = [];

  if (finishedCount > 0) {
    groups.push({
      name: null,
      items: [{ key: FINISHED_KEY, color: GRAY, label: FINISHED_LABEL, count: finishedCount }]
    });
  }

  if (mode === 'street_type') {
    const names = Object.keys(streetTypeColors).sort();
    if (names.length > 0) {
      groups.push({
        name: null,
        items: names.map((name) => ({
          key: 'street_type|' + name,
          color: streetTypeColors[name],
          label: name,
          count: counts['street_type|' + name] || 0
        }))
      });
    }
    return groups;
  }

  Object.keys(urgencyLevels).sort().forEach((name) => {
    const levels = urgencyLevels[name];
    const items = [];

    [1, 2, 3, 4].forEach((level) => {
      if (level === 2 && !levels.level2) return;
      const key = name + '|' + level;
      items.push({
        key: key,
        color: LEVEL_COLORS[level],
        label: levelLabel(levels, level),
        count: counts[key] || 0
      });
    });

    groups.push({ name: name, items: items });
  });

  return groups;
}

function legendTitle(mode) {
  if (mode === 'street_type') return 'Legenda - Cores por Tipo de Via';
  if (mode === 'creation_date') return 'Legenda - Cores por Data de Criação da OS';
  return 'Legenda - Cores por Prazo de Fiscalização';
}

function legendRow(color, label) {
  return '<div class="legend-item" style="margin: 2px 0; display: flex; align-items: center;">' +
    '<span class="map-legend" style="display: inline-block; width: 12px; height: 12px; border-radius: 50%; margin-right: 8px; background-color: ' + color + ';"></span>' +
    '<span>' + label + '</span></div>';
}

function finishedLegendRow() {
  return legendRow(GRAY, 'OSs Concluídas');
}

// Bloco de escala (verde/amarelo/laranja/vermelho) de um tipo de via
function urgencyScaleHtml(name, levels) {
  let html = '<div style="border-left: 3px solid #ddd; padding-left: 10px; margin-left: 10px;">';
  if (name) {
    html += '<strong>' + name + '</strong><br>';
  }
  html += legendRow(GREEN, '≤ ' + levels.level1 + ' dias');
  if (levels.level2) {
    html += legendRow(YELLOW, '≤ ' + levels.level2 + ' dias');
  }
  html += legendRow(ORANGE, '≤ ' + levels.level3 + ' dias');
  html += legendRow(RED, '> ' + levels.level3 + ' dias');
  html += '</div>';
  return html;
}

// streetTypes: { nome: { level1, level2, level3 } }
// options: { mode: 'urgency'|'creation_date', includeFinished: bool }
function urgencyLegendHtml(streetTypes, options = {}) {
  let html = '<h5>' + legendTitle(options.mode) + '</h5>';
  html += '<div style="display: flex; flex-wrap: wrap; gap: 15px; margin-top: 10px;">';
  if (options.includeFinished) {
    html += finishedLegendRow();
  }
  Object.keys(streetTypes).forEach((name) => {
    html += urgencyScaleHtml(name, streetTypes[name]);
  });
  html += '</div>';
  return html;
}

// colorsByName: { nome: cor }
// options: { includeFinished: bool }
function streetTypeLegendHtml(colorsByName, options = {}) {
  let html = '<h5>' + legendTitle('street_type') + '</h5>';
  html += '<div style="display: flex; flex-wrap: wrap; gap: 15px; margin-top: 10px;">';
  if (options.includeFinished) {
    html += finishedLegendRow();
  }
  Object.keys(colorsByName).forEach((name) => {
    html += legendRow(colorsByName[name], name);
  });
  html += '</div>';
  return html;
}

window.ServiceOrderMapColors = {
  markerColor,
  markerBucket,
  legendModelFrom,
  legendTitle,
  legendRow,
  urgencyScaleHtml,
  urgencyLegendHtml,
  streetTypeLegendHtml,
  FALLBACK_LEVELS,
  NO_STREET_TYPE
};
